// Do not use import syntax for axios
// This is intentional to make it work with Rollup
const axios = require('axios');

class HttpClient {
  static middlewares = [];

  static instance = null;

  static getInstance = (config = {}) => {
    if (HttpClient.instance === null) {
      HttpClient.instance = axios.create();
    }

    let instance = HttpClient.instance;
    let middlewares = [...HttpClient.middlewares];

    if (config.middlewares) {
      middlewares.push(...config.middlewares.filter((cm) => cm.key && !middlewares.find((m) => m.key === cm.key)));
    }

    if (config.ignoredMiddlewares) {
      middlewares = middlewares.filter((m) => !config.ignoredMiddlewares.find((key) => key === m.key));
    }

    if (config.middlewares || config.ignoredMiddlewares) {
      instance = axios.create();
    }

    HttpClient.applyMiddlewares(instance, middlewares);

    return instance;
  };

  static get(url, config = {}) {
    const instance = HttpClient.getInstance(config);

    return instance.get(url, config);
  }

  static post(url, data = {}, config = {}) {
    const instance = HttpClient.getInstance(config);

    return instance.post(url, data, config);
  }

  static put(url, data = {}, config = {}) {
    const instance = HttpClient.getInstance(config);

    return instance.put(url, data, config);
  }

  static patch(url, data = {}, config = {}) {
    const instance = HttpClient.getInstance(config);

    return instance.patch(url, data, config);
  }

  static delete(url, config = {}) {
    const instance = HttpClient.getInstance(config);

    return instance.delete(url, config);
  }

  static request(config = {}) {
    const instance = HttpClient.getInstance(config);

    return instance.request(config);
  }

  static use(middleware) {
    if (!middleware.key || HttpClient.middlewares.find((m) => m.key === middleware.key)) {
      return;
    }

    HttpClient.middlewares.push(middleware);
  }

  static applyMiddlewares(instance, middlewares) {
    for (const middleware of middlewares) {
      if (middleware.request) {
        instance.interceptors.request.use(middleware.request);
      }

      if (middleware.response || middleware.error) {
        const responseCallbackHandler = {
          apply: (target, thisArg, argumentsList) => {
            const response = argumentsList[0].response || argumentsList[0];

            return target({
              status: response.status,
              statusText: response.statusText,
              data: response.data,
            });
          },
        };

        instance.interceptors.response.use(
          new Proxy(middleware.response || ((response) => response), responseCallbackHandler),
          new Proxy(middleware.error || ((error) => Promise.reject(error)), responseCallbackHandler)
        );
      }
    }
  }
}

const account = {
  contact: '/users/contact-us/',
  contactSubjects: '/users/contact-us-subjects/',
  basketOffers: '/account/basket-offers/',
  discountItems: '/account/discount-items/',
  expiredBasketOffers: '/account/expired-basket-offers/',
  futureBasketOffers: '/account/future-basket-offers/',
};

const address = {
  base: '/address/',
  countries: '/address/country/',
  city: '/address/city/',
  district: '/address/district/',
  detailed: '/address/detailed/',
  township: '/address/township/',
};

const basket = {
  base: '/baskets/basket/',
  items: '/baskets/basket_items/',
};

const checkout = {
  base: '/orders/checkout/',
  addGift: '/orders/checkout/?page=GiftBoxPage',
  removeGift: '/orders/checkout/?page=GiftBoxIndexPage',
};

const contracts = {
  base: '/orders/contract/',
};

const misc = {
  csrfToken: '/csrf_token/',
  autocomplete: '/autocomplete/',
  stores: 'address/stores/',
  subscriptions: '/subscriptions/',
  menu: '/menus/generate/',
  config: '/config/'
};

const order = {
  base: '/users/orders/',
  cancellationReason: '/users/orders/cancellation_reasons/',
  cancellationRequest: '/users/orders/cancellation_requests/',
  autoCancelRequest: '/orders/$0/cancel/',
  bulkCancellationRequest: '/users/orders/bulk_cancellation_requests/',
};

const product = {
  base: '/product/',
  list: '/list/',
  stockAlert: '/wishlists/product-alerts/',
  priceAlert: '/wishlists/price-alerts/',
  favourite: '/wishlists/favourite-products/',
  retailStock: '/retail_store_stock/',
};

const user = {
  currentUser: '/current_user/',
  login: '/users/login/',
  logout: '/users/logout/',
  register: '/users/register/',
  emailChange: '/users/email-change/',
  passwordChange: '/users/password/change/',
  passwordReset: '/users/password/reset/',
  profile: '/users/profile/',
  guestLogin: '/orders/checkout/?page=IndexPage',
  collection: '/wishlists/user-collection/',
  collections: '/wishlists/user-collections/',
  collectionItems: '/wishlists/user-collection-items/',
};

var urls = {
  account,
  address,
  basket,
  checkout,
  contracts,
  misc,
  order,
  product,
  user,
};

const AccountService = {
  extend: (key, fn) => {
    AccountService[key] = fn;
  },
  postContactForm: ({
    full_name, email, phone, subject, order, message
  }) => {
    const data = new URLSearchParams();

    data.append('full_name', full_name);
    data.append('email', email);
    data.append('phone', phone);
    data.append('subject', subject);
    data.append('order', order);
    data.append('message', message);

    return HttpClient.post(
      urls.account.contact,
      data,
      {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
  },
  getContactSubjects: () => {
    return HttpClient.get(urls.account.contactSubjects);
  },
  getBasketOffers: () => {
    return HttpClient.get(urls.account.basketOffers);
  },
  getDiscountItems: () => {
    return HttpClient.get(urls.account.discountItems);
  },
  getExpiredOffers: () => {
    return HttpClient.get(urls.account.expiredBasketOffers);
  },
  getFutureOffers: () => {
    return HttpClient.get(urls.account.futureBasketOffers);
  }
};

const BasketService = {
  extend: (key, fn) => {
    BasketService[key] = fn;
  },
  getBasket: () => {
    return HttpClient.get(urls.basket.base);
  },
  addProduct: ({ product, quantity = 1, attributes = {} }) =>
    HttpClient.post(urls.basket.base, { product, quantity, attributes }),
  setDiscount: ({ voucher_code }) =>
    HttpClient.patch(urls.basket.base, { voucher_code }),
  removeDiscount: () =>
    HttpClient.patch(urls.basket.base, { remove_voucher_code: true }),
  setQuantity: ({ product, quantity, attributes = {} }) =>
    HttpClient.put(urls.basket.base, { product, quantity, attributes }),
  removeProduct: ({ product, quantity = 0, attributes = {} }) =>
    HttpClient.put(urls.basket.base, { product, quantity, attributes }),
  setGiftNote: ({ id, gift_note, is_all = true }) => {
    return HttpClient.patch(urls.basket.items, {
      attributes: {
        gift_note
      },
      id,
      is_all
    });
  }
};

const CheckoutService = {
  extend: (key, fn) => {
    CheckoutService[key] = fn;
  },
  getCheckout: () => {
    return HttpClient.get(urls.checkout.base);
  },
  getAddresses: () => {
    return HttpClient.get(urls.checkout.base, {
      params: {
        page: "AddressSelectionPage",
      },
    });
  },
  setAddress: ({ shipping_address, billing_address }) => {
    const data = new URLSearchParams();

    data.append("shipping_address", shipping_address);
    data.append("billing_address", billing_address);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "AddressSelectionPage",
      },
    });
  },
  setShippingOption: () => {
    // no shipping option parameter?
    const data = new URLSearchParams();

    data.append("shipping_option", shipping_option);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "ShippingOptionSelectionPage",
      },
    });
  },
  setDeliveryOption: ({ delivery_option, clear = false }) => {
    const data = new URLSearchParams();

    data.append("delivery_option", delivery_option);
    data.append("clear", clear);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "DeliveryOptionSelectionPage",
      },
    });
  },
  setPaymentOption: ({ payment_option }) => {
    const data = new URLSearchParams();

    data.append("payment_option", payment_option);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "PaymentOptionSelectionPage",
      },
    });
  },
  setBinNumber: ({ bin_number }) => {
    const data = new URLSearchParams();

    data.append("bin_number", bin_number);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "BinNumberPage",
      },
    });
  },
  setInstallment: ({ installment }) => {
    const data = new URLSearchParams();

    data.append("installment", installment);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "InstallmentSelectionPage",
      },
    });
  },
  setGPay: () => {
    return HttpClient.post(urls.checkout.base, null, {
      params: {
        page: "GPaySelectionPage",
      },
    });
  },
  setFundsTransferAccount: ({ bank_account }) => {
    const data = new URLSearchParams();

    data.append("bank_account", bank_account);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "FundsTransferChoicePage",
      },
    });
  },
  setCreditPaymentOption: ({ credit_payment_option }) => {
    const data = new URLSearchParams();

    data.append("credit_payment_option", credit_payment_option);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "CreditPaymentSelectionPage",
      },
    });
  },
  setPayOnDeliveryMethod: ({ payment_choice }) => {
    const data = new URLSearchParams();

    data.append("payment_choice", payment_choice);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "PayOnDeliveryPaymentChoicePage",
      },
    });
  },
  setSampleProducts: ({ products }) => {
    return HttpClient.post(
      urls.checkout.base,
      products.map((p) => `sample_products=${p}`).join("&"),
      {
        params: {
          page: "SampleProductPage",
        },
      }
    );
  },
  getLoyalty: () => {
    return HttpClient.get(urls.checkout.base, {
      params: {
        page: "LoyaltyCardPage",
      },
    });
  },
  setLoyalty: ({ selected_loyalty_amount }) => {
    const data = new URLSearchParams();

    data.append("selected_loyalty_amount", selected_loyalty_amount);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "LoyaltyCardPage",
      },
    });
  },
  setRetailStore: ({ retail_store, billing_address }) => {
    const data = new URLSearchParams();

    data.append("retail_store", retail_store);
    data.append("billing_address", billing_address);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "RetailStoreSelectionPage",
      },
    });
  },
  setGiftBox: ({ note }) => {
    const data = new URLSearchParams();

    data.append("note", note);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "GiftBoxPage",
      },
    });
  },
  deleteGiftBox: () => {
    return HttpClient.post(urls.checkout.base, null, {
      params: {
        page: "GiftBoxIndexPage",
      },
    });
  },
  creditPaymentConfirmation: ({ creditPaymentData }) => {
    const data = new URLSearchParams();

    data.append("creditPaymentData", creditPaymentData);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "CreditPaymentConfirmationPage",
      },
    });
  },
  completeCheckout: ({
    agreement,
    card_holder,
    card_number,
    card_month,
    card_year,
    card_cvv,
    use_three_d,
  }) => {
    const data = new URLSearchParams();

    data.append("agreement", agreement);
    data.append("card_holder", card_holder);
    data.append("card_number", card_number);
    data.append("card_month", card_month);
    data.append("card_year", card_year);
    data.append("card_cvv", card_cvv);
    data.append("use_three_d", Boolean(use_three_d));

    return HttpClient.post(urls.checkout.base, data, {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "*/*",
        "X-Requested-With": "XMLHttpRequest",
      },
    });
  },
  completeFundsTransfer: ({ agreement }) => {
    const data = new URLSearchParams();

    data.append("agreement", agreement);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "FundsTransferPage",
      },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "*/*",
        "X-Requested-With": "XMLHttpRequest",
      },
    });
  },
  completePayOnDelivery: ({ agreement }) => {
    const data = new URLSearchParams();

    data.append("agreement", agreement);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "PayOnDeliveryPage",
      },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "*/*",
        "X-Requested-With": "XMLHttpRequest",
      },
    });
  },
  completeGPay: () => {
    return HttpClient.post(urls.checkout.base, null, {
      params: {
        page: "GPayRedirectPage",
      },
    });
  },
  completeRedirectionPayment: ({ agreement }) => {
    const data = new URLSearchParams();

    data.append("agreement", agreement);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "RedirectionPaymentSelectedPage",
      },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "*/*",
        "X-Requested-With": "XMLHttpRequest",
      },
    });
  },
  getRewards: ({
    payment_option,
    card_number,
    card_year,
    card_month,
    card_cvv,
  }) => {
    const data = new URLSearchParams();

    data.append("payment_option", payment_option);
    data.append("card_number", card_number);
    data.append("card_year", card_year);
    data.append("card_month", card_month);
    data.append("card_cvv", card_cvv);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "RewardListPage",
      },
    });
  },
  setRewards: ({ general, special }) => {
    const data = new URLSearchParams();

    general >= 0 && data.append("general", general);
    special >= 0 && data.append("special", special);

    return HttpClient.post(urls.checkout.base, data, {
      params: {
        page: "RewardSelectionPage",
      },
    });
  },
  getCheckoutProviders: () => {
    return HttpClient.post(
      urls.checkout.base,
      {},
      {
        params: {
          page: "CheckoutProviderIndexPage",
        },
      }
    );
  },
  selectCheckoutProvider: ({ checkout_provider }) => {
    const data = new URLSearchParams();

    data.append("checkout_provider", checkout_provider);
      
    return HttpClient.post(
      urls.checkout.base,
      data,
      {
        params: {
          page: "CheckoutProviderSelectionPage"
        },
      }
    );
  },
};

const ProductService = {
  extend: (key, fn) => {
    ProductService[key] = fn;
  },
  getProduct: ({ pk, params = {} }) =>
    HttpClient.get(`${urls.product.base}${pk}/`, { params }),
  getFavourites: ({ limit, page }) =>
    HttpClient.get(urls.product.favourite, {
      params: {
        limit,
        page
      }
    }),
  addFavourite: ({ product }) =>
    HttpClient.post(urls.product.favourite, { product }),
  deleteFavourite: ({ favPk }) =>
    HttpClient.delete(`${urls.product.favourite}${favPk}/`),
  fetchFavourites: ({ limit = 12, page = 1 } = {}) =>
    HttpClient.get(`${urls.product.favourite}?limit=${limit}&page=${page}`),
  stockAlert: ({ product }) =>
    HttpClient.post(urls.product.stockAlert, { product }),
  getRetailStoreStock: ({ productPk, queryString }) =>
    HttpClient.get(`${urls.product.retailStock}${productPk}/?${queryString}`),
  getPriceNotice: ({ productPk }) => {
    return HttpClient.get(urls.product.priceAlert, {
      params: {
        product: productPk
      }
    })
  }
};

const UserService = {
  extend: (key, fn) => {
    UserService[key] = fn;
  },
  register: ({
    email, first_name, last_name, date_of_birth, password, phone, sms_allowed, email_allowed, gender, confirm, kvkk_confirm, code, resend
  }) =>
    HttpClient.post(urls.user.register, {
      email, first_name, last_name, date_of_birth, password, phone, sms_allowed, email_allowed, gender, confirm, kvkk_confirm, code, resend
    }),
  login: ({ email, password }) =>
    HttpClient.post(urls.user.login, { email, password }),
  logout: () => {
    return HttpClient.post(urls.user.logout);
  },
  guest: ({ user_email, phone }) => {
    const params = new URLSearchParams();

    params.append('user_email', user_email);
    params.append('phone', phone);
    return HttpClient.post(urls.user.guestLogin, params,
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } });
  },
  changeEmail: ({ email, password }) =>
    HttpClient.post(urls.user.emailChange, { email, password }),
  changePassword: ({ old_password, new_password1, new_password2 }) =>
    HttpClient.post(urls.user.passwordChange, { old_password, new_password1, new_password2 }),
  resetPassword: ({ email }) =>
    HttpClient.post(urls.user.passwordReset, { email }),
  updateProfile: ({
    first_name, last_name, email, phone, gender, date_of_birth, email_allowed, sms_allowed
  }) =>
    HttpClient.patch(urls.user.profile, {
      first_name, last_name, email, phone, gender, date_of_birth, email_allowed, sms_allowed
    }),
  getCurrentUser: () => {
    return HttpClient.get(urls.user.currentUser);
  },
  getAllCollections: () =>
    HttpClient.get(urls.user.collections),
  searchCollections: ({ searchByName, searchByProductPk } = {}) =>
    HttpClient.get(`${urls.user.collections}?search=${searchByName}&product_id=${searchByProductPk}`),
  getCollection: ({ collectionPk }) =>
    HttpClient.get(`${urls.user.collections}${collectionPk}/`),
  createCollection: ({ collectionName }) =>
    HttpClient.post(urls.user.collections, { name: collectionName }),
  updateCollection: ({ collectionPk, collectionName, collectionStatus }) =>
    HttpClient.patch(`${urls.user.collections}${collectionPk}/`, { name: collectionName, status: collectionStatus }),
  deleteCollection: ({ collectionPk }) =>
    HttpClient.delete(`${urls.user.collections}${collectionPk}/`),
  addProductToCollection: ({ productPk, collectionPk }) =>
    HttpClient.post(urls.user.collectionItems, { product_id: productPk, usercollection_id: collectionPk }),
  removeProductFromCollection: ({ collectionItemPk }) =>
    HttpClient.delete(`${urls.user.collectionItems}${collectionItemPk}/`),
  getPublicCollection: ({ collectionSlug }) =>
    HttpClient.get(`${urls.user.collection}${collectionSlug}/`),
};

const AddressService = {
  extend: (key, fn) => {
    AddressService[key] = fn;
  },
  createAddress: ({
    country,
    is_corporate,
    type,
    title,
    first_name,
    last_name,
    phone_number,
    city,
    township,
    district,
    line,
    postcode,
    company_name,
    tax_no,
    tax_office,
    e_bill_taxpayer
  }) =>
    HttpClient.post(urls.address.base, {
      country,
      is_corporate,
      type,
      title,
      first_name,
      last_name,
      phone_number,
      city,
      township,
      district,
      line,
      postcode,
      company_name,
      tax_no,
      tax_office,
      e_bill_taxpayer
    }),
  updateAddress: ({
    pk,
    country,
    is_corporate,
    type,
    title,
    first_name,
    last_name,
    phone_number,
    city,
    township,
    district,
    line,
    postcode,
    company_name,
    tax_no,
    tax_office,
    e_bill_taxpayer
  }) =>
    HttpClient.patch(`${urls.address.base}${pk}/`, {
      pk,
      country,
      is_corporate,
      type,
      title,
      first_name,
      last_name,
      phone_number,
      city,
      township,
      district,
      line,
      postcode,
      company_name,
      tax_no,
      tax_office,
      e_bill_taxpayer
    }),
  getAddress: (addressPk) =>
    HttpClient.get(`${urls.address.base}${addressPk}/`),
  deleteAddress: (addressPk) =>
    HttpClient.delete(`${urls.address.base}${addressPk}/`),
  getCountries: async () =>
    HttpClient.get(`${urls.address.countries}`),
  getCountry: async () => {
    const { data: config } = await HttpClient.get(urls.misc.config);

    return { data: config?.country?.pk }
  },
  getCities: (country = 1, limit = 9999) =>
    HttpClient.get(`${urls.address.city}?country=${country}&limit=${limit}/`),
  getTownships: (city, limit = 9999) =>
    HttpClient.get(`${urls.address.township}?city=${city}&limit=${limit}/`),
  getDistricts: (township, limit = 9999) =>
    HttpClient.get(`${urls.address.district}?township=${township}&limit=${limit}/`),
  getStores: () =>
    HttpClient.get(`${urls.address.city}?retailstore__isnull=false`),
  getAddresses: () =>
    HttpClient.get(`${urls.address.detailed}`),
  setDefaultAddress: (addressPk) =>
    HttpClient.patch(`${urls.address.base}${addressPk}/`, { primary: true })
};

const OrderService = {
  extend: (key, fn) => {
    OrderService[key] = fn;
  },
  getCancellationReasons: () => HttpClient.get(urls.order.cancellationReason),
  sendCancellationRequest: ({
    order_item,
    reason,
    cancellation_type,
    description,
    iban,
    holder_name
  }) =>
    HttpClient.post(urls.order.cancellationRequest, {
      order_item,
      reason,
      cancellation_type,
      description,
      iban,
      holder_name
    }),
  sendAutoCancellationRequest: ({ orderNumber, cancel_order_items }) =>
    HttpClient.post(urls.order.autoCancelRequest.replace('$0', orderNumber), { cancel_order_items }),
  sendBulkCancellationRequest: ({ cancel_order_items }) =>
    HttpClient.post(urls.order.bulkCancellationRequest, { cancel_order_items }),
  getOrders: () => HttpClient.get(urls.order.base),
  getOrder: ({ orderNumber }) => {
    return HttpClient.get(urls.order.base, {
      params: `${orderNumber}/`
    })
  }
};

const MiscService = {
  extend: (key, fn) => {
    MiscService[key] = fn;
  },
  searchAutocomplete: (query) =>
    HttpClient.get(`${urls.misc.autocomplete}?search_text=${query}`),
  getCSRFToken: () => {
    return HttpClient.get(urls.misc.csrfToken);
  },
  postSubscription: ({ email }) => {
    return HttpClient.post(urls.misc.subscriptions, { email });
  },
  getStores: () => {
    return HttpClient.get(urls.misc.stores)
  },
  getStoresByCoordinates: ({ latitude, longitude, nearest = false }) => {
    return HttpClient.get(urls.misc.stores, {
      latitude,
      longitude,
      nearest,
      radius: 1,
      quantity: 20,
    });
  },
  getMenu: ({ depth_height = 3 }) => {
    return HttpClient.get(urls.misc.menu, { depth_height })
  },
  getConfig: () => {
    return HttpClient.get(urls.misc.config)
  },
  getPageHtml: ({ url }) => {
    return HttpClient.get(url, {
      responseType: 'text',
      headers: {
        'Accept': 'text/html'
      }
    });
  }
};

var Services = /*#__PURE__*/Object.freeze({
  __proto__: null,
  AccountService: AccountService,
  BasketService: BasketService,
  CheckoutService: CheckoutService,
  ProductService: ProductService,
  UserService: UserService,
  AddressService: AddressService,
  OrderService: OrderService,
  MiscService: MiscService
});

async function callService(service, action, params = {}) {
  return await Services[`${service}Service`][action](params);
}

class Analytics {
  adapters = [];
  events = [];
  adaptersInitialized = false;

  addAdapter(adapter) {
    this.adapters.push(adapter);
  };

  pushEvent(action) {
    if (this.adaptersInitialized) {
      this.publishToAdapters(action);
      return;
    }

    this.events.push(action);
  };

  publishToAdapters(action) {
    for (const adapter of this.adapters) {
      adapter.push && adapter.push(action);
    }
  };

  async initAdapters() {
    if (!this.adapters.length) {
      return;
    }

    try {
      await Promise.all(this.adapters.map(adapter => adapter.loadScript()));

      this.adaptersInitialized = true;

      for (const event of this.events) {
        this.publishToAdapters(event);
      }

      this.events = [];
    } catch (error) {
      console.log(error);
    }
  }
}

const analytics = new Analytics();

const identify = 'identify';
const pageViewed = 'pageViewed';
const productListViewed$1 = 'productListViewed';
const productClicked$1 = 'productClicked';
const productViewed$1 = 'productViewed';
const productAdded$1 = 'productAdded';
const productRemoved$1 = 'productRemoved';
const promotionViewed$1 = 'promotionViewed';
const promotionClick$1 = 'promotionClick';
const cartViewed$1 = 'cartViewed';
const gtmStart = 'gtmStart';
const cartDetailEvent$1 = 'cartDetailEvent';
const checkoutStep$1 = 'checkoutStep';
const checkoutStepViewed$1 = 'checkoutStepViewed';
const checkoutStepViewedPayment$1 = 'checkoutStepViewedPayment';
const orderCompleted$1 = 'orderCompleted';
const loginAction$1 = 'loginAction';
const signUpAction$1 = 'signUpAction';

var actionTypes = /*#__PURE__*/Object.freeze({
  __proto__: null,
  identify: identify,
  pageViewed: pageViewed,
  productListViewed: productListViewed$1,
  productClicked: productClicked$1,
  productViewed: productViewed$1,
  productAdded: productAdded$1,
  productRemoved: productRemoved$1,
  promotionViewed: promotionViewed$1,
  promotionClick: promotionClick$1,
  cartViewed: cartViewed$1,
  gtmStart: gtmStart,
  cartDetailEvent: cartDetailEvent$1,
  checkoutStep: checkoutStep$1,
  checkoutStepViewed: checkoutStepViewed$1,
  checkoutStepViewedPayment: checkoutStepViewedPayment$1,
  orderCompleted: orderCompleted$1,
  loginAction: loginAction$1,
  signUpAction: signUpAction$1
});

const pageCategory = window.location.pathname.replace(/\//g, "");

const common = {
  Category: 'Enhanced Ecommerce',
  Value: 0,
};

const productClicked = {
  common: {
    Label: 'Product Click',
    noninteraction: false,
    event: 'eeEvent',
    Action: 'Browse'
  },
  typeKey: 'click',
  actionField: {
    list: pageCategory
  },
  valueType: {}
};

const productAdded = {
  common: {
    Label: 'Product Add to Cart',
    noninteraction: false,
    event: 'eeCart',
    Action: 'Browse'
  },
  typeKey: 'add',
  valueType: {}
};

const productViewed = {
  common: {
    Label: 'Product Detail Viewed',
    noninteraction: true,
    event: 'eeEvent',
    Action: 'Browse',
  },
  typeKey: 'detail',
  actionField: {
    action: "detail",
  },
  valueType: {}
};

const productListViewed = {
  common: {
    Label: 'Product Impressions',
    noninteraction: true,
    event: 'eeEvent',
    Action: 'Browse',
  },
  typeKey: 'impressions',
  valueType: []
};

const productRemoved = {
  common: {
    Label: 'Product Remove from Cart',
    noninteraction: false,
    event: 'eeCart',
    Action: 'Browse'
  },
  typeKey: 'remove',
  valueType: {}
};

const cartViewed = {
  common: {
    Label: 'Sepet',
    noninteraction: true,
    event: 'eeCheckout',
    Action: 'Checkout'
  },
  typeKey: 'checkout',
  actionField: {
    action: 'checkout',
    step: 1
  },
  valueType: {}
};

const checkoutStep = {
  common: {
    Label: 'Login',
    noninteraction: true,
    event: 'eeCheckout',
    Action: 'Checkout'
  },
  typeKey: 'checkout',
  actionField: {
    action: 'checkout',
    step: 2
  },
  valueType: {}
};

const checkoutStepViewed = {
  common: {
    Label: 'Adres',
    noninteraction: true,
    event: 'eeCheckout',
    Action: 'Checkout'
  },
  typeKey: 'checkout',
  actionField: {
    action: 'checkout',
    step: 3
  },
  valueType: {}
};

const checkoutStepViewedPayment = {
  common: {
    Label: 'Ödeme',
    noninteraction: true,
    event: 'eeCheckout',
    Action: 'Checkout'
  },
  typeKey: 'checkout',
  actionField: {
    action: 'checkout',
    step: 4
  },
  valueType: {}
};

const orderCompleted = {
  common: {
    Label: 'Purchase',
    noninteraction: true,
    event: 'eepurchase',
    Action: 'Checkout'
  },
  typeKey: 'purchase',
  valueType: {}
};

const cartDetailEvent = {
  common: {
    Label: 'Product Attributes',
    noninteraction: true,
    event: 'cartDetailEvent',
    Action: 'Basket Product'
  },
  typeKey: 'cartProducts',
  valueType: []
};

const promotionViewed = {
  common: {
    Label: 'Promotion Impressions',
    noninteraction: true,
    event: 'eeEvent',
    Action: 'Browse'
  },
  typeKey: 'promoView',
  valueType: {}
};

const promotionClick = {
  common: {
    Label: 'Promotion Click',
    noninteraction: true,
    event: 'eeEvent',
    Action: 'Browse'
  },
  typeKey: 'promoClick',
  valueType: {}
};

const loginAction = {
  common: {
    'Category': 'Member',
    'Action': 'Login',
    'Label': new Date(),
    'event': 'gaEvent'
  }
};

const signUpAction = {
  common: {
    'Category': 'Member',
    'Action': 'Sign Up',
    'Label': new Date(),
    'event': 'gaEvent'
  }
};

var actionParams = /*#__PURE__*/Object.freeze({
  __proto__: null,
  common: common,
  productClicked: productClicked,
  productAdded: productAdded,
  productViewed: productViewed,
  productListViewed: productListViewed,
  productRemoved: productRemoved,
  cartViewed: cartViewed,
  checkoutStep: checkoutStep,
  checkoutStepViewed: checkoutStepViewed,
  checkoutStepViewedPayment: checkoutStepViewedPayment,
  orderCompleted: orderCompleted,
  cartDetailEvent: cartDetailEvent,
  promotionViewed: promotionViewed,
  promotionClick: promotionClick,
  loginAction: loginAction,
  signUpAction: signUpAction
});

class GoogleAnalytics {
  constructor(config) {
    this.config = config;
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.key}`;

      script.addEventListener('load', () => {
        window.dataLayer = window.dataLayer || [];

        function gtag() {
          dataLayer.push(arguments);
        }
        gtag('js', new Date());
        gtag('config', this.config.key);
        resolve();
      });

      script.addEventListener('error', e => reject(e.error));

      document.head.appendChild(script);
    });
  }
}

class GoogleTagManager {
  events = new Map([
    [cartViewed$1, cartViewed],
    [productRemoved$1, productRemoved],
    [checkoutStep$1, checkoutStep],
    [productViewed$1, productViewed],
    [productListViewed$1, productListViewed],
    [checkoutStepViewed$1, checkoutStepViewed],
    [checkoutStepViewedPayment$1, checkoutStepViewedPayment],
    [orderCompleted$1, orderCompleted],
    [productAdded$1, productAdded],
    [productClicked$1, productClicked],
    [cartDetailEvent$1, cartDetailEvent],
    [promotionViewed$1, promotionViewed],
    [promotionClick$1, promotionClick],
    [loginAction$1, loginAction],
    [signUpAction$1, signUpAction],
  ]);

  constructor(config) {
    this.config = config;
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');

      script.src = `https://www.googletagmanager.com/gtm/js?id=${this.config.key}`;
      script.addEventListener('load', resolve);
      script.addEventListener('error', e => reject(e.error));

      document.head.appendChild(script);
    });
  }

  push(action) {
    if (action.type === identify) {
      this.identify(action.data, action.extraData);
      return;
    }

    if (action.type === pageViewed) {
      this.pageViewed(action.data);
      return;
    }

    if (action.type === gtmStart) {
      this.gtmStart();
      return;
    }

    this.track(action);
  }

  identify(userData, extraData = {}) {
    const {
      id,
      user: {
        email,
        signupDate,
        gender,
        permission,
      }
    } = userData;

    const data = {
      cd_userId: id,
      cd_userEmail: email,
      cd_userSignupDate: signupDate,
      cd_userGender: gender,
      cd_userEmailPermission: permission
    };

    for (const key in extraData) {
      if (Object.hasOwnProperty.call(extraData, key)) {
        data[key] = extraData[key];
      }
    }

    window.dataLayer.push(data);
  }

  pageViewed(pageData) {
    const cd_pageCategory = window.location.pathname.split('/')[1];
    const data = {
      cd_pageType: pageData,
      cd_pageCategory
    };

    if (typeof pageData === 'object') {
      let { pageType, pageBreadcrumb } = pageData;
      data.cd_pageType = pageType;

      if (pageBreadcrumb.length) {
        data.cd_pageBreadcrumb = pageBreadcrumb;
      }
    }

    window.dataLayer.push(data);
  }

  gtmStart() {
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js'
    });
  }

  track(action) {
    if (!action.type) {
      return;
    }

    const params = this.events.get(action.type);

    if (!params.typeKey) {
      const data = {
        ...common,
        ...params.common,
      };

      window.dataLayer.push(data);
      return;
    }

    const data = {
      ...common,
      ...params.common,
      ecommerce: {
        [params.typeKey]: params.valueType instanceof Array ?
          [...action.payload] : { ...action.payload },
      }
    };

    if (params.actionField) {
      data.ecommerce[params.typeKey] = {
        actionField: { ...params.actionField },
        ...data.ecommerce[params.typeKey]
      };
    }

    window.dataLayer.push(data);
  }
}

export { AccountService, AddressService, BasketService, CheckoutService, GoogleAnalytics, GoogleTagManager, HttpClient, MiscService, OrderService, ProductService, UserService, actionParams, actionTypes, analytics, callService, urls };
